/**
 * Mortgage API Service
 * ─────────────────────────────────────────────────────────────────────────────
 * Production-ready connector for professor's mortgage backend.
 *
 * SWITCHING TO PRODUCTION:
 *   1. Set VITE_MORTGAGE_USE_MOCK=false in .env
 *   2. Set VITE_MORTGAGE_API_BASE_URL=https://your-professors-backend.com/v1
 *   3. Confirm endpoint paths in mortgageApiConfig.js match professor's routes
 *   4. Confirm auth header format matches professor's backend expectations
 * ─────────────────────────────────────────────────────────────────────────────
 */

import axios from 'axios';
import MORTGAGE_API_CONFIG from './mortgageApiConfig';
import { MOCK_AUTH, MOCK_MORTGAGE, MOCK_UTILITY } from './mortgageMockData';

// ─── Token Management ─────────────────────────────────────────────────────────

const tokenManager = {
  getToken: () => localStorage.getItem(MORTGAGE_API_CONFIG?.TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(MORTGAGE_API_CONFIG?.REFRESH_TOKEN_KEY),
  setTokens: (token, refreshToken) => {
    if (token) localStorage.setItem(MORTGAGE_API_CONFIG?.TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(MORTGAGE_API_CONFIG?.REFRESH_TOKEN_KEY, refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem(MORTGAGE_API_CONFIG?.TOKEN_KEY);
    localStorage.removeItem(MORTGAGE_API_CONFIG?.REFRESH_TOKEN_KEY);
  },
  isAuthenticated: () => !!localStorage.getItem(MORTGAGE_API_CONFIG?.TOKEN_KEY),
};

// ─── Axios Instance ───────────────────────────────────────────────────────────

const apiClient = axios?.create({
  baseURL: MORTGAGE_API_CONFIG?.BASE_URL,
  timeout: MORTGAGE_API_CONFIG?.TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Request Interceptor ──────────────────────────────────────────────────────

apiClient?.interceptors?.request?.use(
  (config) => {
    const token = tokenManager?.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach request timestamp for timeout tracking
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => Promise.reject(normalizeError(error))
);

// ─── Response Interceptor ─────────────────────────────────────────────────────

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue?.forEach((prom) => {
    if (error) prom?.reject(error);
    else prom?.resolve(token);
  });
  failedQueue = [];
};

apiClient?.interceptors?.response?.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    // Handle 401 — attempt token refresh once
    if (error?.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })?.then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })?.catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = tokenManager?.getRefreshToken();
        const { data } = await apiClient?.post(MORTGAGE_API_CONFIG?.ENDPOINTS?.AUTH?.REFRESH, { refreshToken });
        tokenManager?.setTokens(data?.token);
        processQueue(null, data?.token);
        originalRequest.headers.Authorization = `Bearer ${data?.token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenManager?.clearTokens();
        return Promise.reject(normalizeError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normalizeError(error));
  }
);

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const normalizeError = (error) => {
  // Already a normalized mock error
  if (error?.code && !error?.isAxiosError) return error;

  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message || error?.response?.data?.error;

  const errorMap = {
    400: { code: 'BAD_REQUEST', message: serverMessage || 'Invalid request. Please check your inputs.' },
    401: { code: 'UNAUTHORIZED', message: serverMessage || 'Authentication required. Please log in.' },
    403: { code: 'FORBIDDEN', message: serverMessage || 'You do not have permission to perform this action.' },
    404: { code: 'NOT_FOUND', message: serverMessage || 'The requested resource was not found.' },
    409: { code: 'CONFLICT', message: serverMessage || 'A conflict occurred with the current state.' },
    422: { code: 'VALIDATION_ERROR', message: serverMessage || 'Validation failed. Please check your inputs.', fields: error?.response?.data?.fields },
    429: { code: 'RATE_LIMITED', message: 'Too many requests. Please wait a moment and try again.' },
    500: { code: 'SERVER_ERROR', message: 'The server encountered an error. Please try again later.' },
    502: { code: 'BAD_GATEWAY', message: 'The server is temporarily unavailable. Please try again.' },
    503: { code: 'SERVICE_UNAVAILABLE', message: 'The mortgage service is currently unavailable. Please try again later.' },
    504: { code: 'GATEWAY_TIMEOUT', message: 'The request timed out. Please check your connection and try again.' },
  };

  if (error?.code === 'ECONNABORTED') {
    return { code: 'TIMEOUT', message: `Request timed out after ${MORTGAGE_API_CONFIG?.TIMEOUT_MS / 1000}s. Please try again.` };
  }

  if (!error?.response) {
    return { code: 'NETWORK_ERROR', message: 'Unable to connect to the mortgage service. Please check your internet connection.' };
  }

  return errorMap?.[status] || { code: 'UNKNOWN_ERROR', message: serverMessage || 'An unexpected error occurred.' };
};

// ─── Retry Logic ──────────────────────────────────────────────────────────────

const withRetry = async (fn, retries = MORTGAGE_API_CONFIG?.MAX_RETRIES, delay = MORTGAGE_API_CONFIG?.RETRY_DELAY_MS) => {
  const RETRYABLE_CODES = ['NETWORK_ERROR', 'TIMEOUT', 'SERVICE_UNAVAILABLE', 'BAD_GATEWAY', 'GATEWAY_TIMEOUT'];
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && RETRYABLE_CODES?.includes(error?.code)) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay * 1.5); // Exponential backoff
    }
    throw error;
  }
};

// ─── API Request Wrapper ──────────────────────────────────────────────────────

const request = async (method, endpoint, data = null, options = {}) => {
  return withRetry(async () => {
    const config = {
      method,
      url: endpoint,
      ...(data && (method === 'get' ? { params: data } : { data })),
      ...options,
    };
    const response = await apiClient(config);
    return response?.data;
  });
};

// ─── Auth Service ─────────────────────────────────────────────────────────────

export const mortgageAuthService = {
  /**
   * Login with email and password.
   * @param {string} email
   * @param {string} password
   */
  login: async ({ email, password }) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) {
      const result = await MOCK_AUTH?.login({ email, password });
      tokenManager?.setTokens(result?.token, result?.refreshToken);
      return result;
    }
    const result = await request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.AUTH?.LOGIN, { email, password });
    tokenManager?.setTokens(result?.token, result?.refreshToken);
    return result;
  },

  /**
   * Refresh the access token using the stored refresh token.
   */
  refreshToken: async () => {
    const refreshToken = tokenManager?.getRefreshToken();
    if (MORTGAGE_API_CONFIG?.USE_MOCK) {
      const result = await MOCK_AUTH?.refresh({ refreshToken });
      tokenManager?.setTokens(result?.token);
      return result;
    }
    const result = await request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.AUTH?.REFRESH, { refreshToken });
    tokenManager?.setTokens(result?.token);
    return result;
  },

  /**
   * Logout and clear stored tokens.
   */
  logout: async () => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) {
      const result = await MOCK_AUTH?.logout();
      tokenManager?.clearTokens();
      return result;
    }
    const result = await request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.AUTH?.LOGOUT);
    tokenManager?.clearTokens();
    return result;
  },

  /**
   * Get the authenticated user's profile.
   */
  getProfile: async () => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_AUTH?.profile();
    return request('get', MORTGAGE_API_CONFIG?.ENDPOINTS?.AUTH?.PROFILE);
  },

  isAuthenticated: tokenManager?.isAuthenticated,
  getToken: tokenManager?.getToken,
};

// ─── Mortgage Service ─────────────────────────────────────────────────────────

export const mortgageService = {
  /**
   * Check mortgage eligibility.
   * @param {object} params - { annualIncome, monthlyExpenses, deposit, propertyPrice, loanTerm, creditScore }
   */
  checkEligibility: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.eligibilityCheck(params);
    return request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.ELIGIBILITY_CHECK, params);
  },

  /**
   * Calculate monthly/fortnightly/weekly repayments.
   * @param {object} params - { loanAmount, interestRate, loanTerm, repaymentType }
   */
  calculatePayment: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.calculatePayment(params);
    return request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.PAYMENT_CALCULATOR, params);
  },

  /**
   * Submit a full mortgage application.
   * @param {object} params - { applicantDetails, propertyDetails, financialDetails }
   */
  applyForApproval: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.applyForApproval(params);
    return request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.APPROVAL_PROCESS, params);
  },

  /**
   * Analyse refinancing options.
   * @param {object} params - { currentLoanBalance, currentInterestRate, currentLoanTerm, newInterestRate, newLoanTerm, propertyValue }
   */
  analyseRefinance: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.refinanceAnalysis(params);
    return request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.REFINANCE_ANALYSIS, params);
  },

  /**
   * Get current lender rates.
   * @param {object} params - { loanAmount, loanTerm, loanType }
   */
  getRates: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.getRates(params);
    return request('get', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.RATE_COMPARISON, params);
  },

  /**
   * Quick affordability check — returns max borrowing power.
   * @param {object} params - { annualIncome, monthlyExpenses, deposit }
   */
  checkAffordability: async (params) => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_MORTGAGE?.affordabilityCheck(params);
    return request('post', MORTGAGE_API_CONFIG?.ENDPOINTS?.MORTGAGE?.AFFORDABILITY, params);
  },
};

// ─── Utility Service ──────────────────────────────────────────────────────────

export const mortgageUtilityService = {
  /**
   * Health check — verify backend is reachable.
   */
  healthCheck: async () => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_UTILITY?.healthCheck();
    return request('get', MORTGAGE_API_CONFIG?.ENDPOINTS?.UTILITY?.HEALTH_CHECK);
  },

  /**
   * Get list of supported lenders.
   */
  getLenders: async () => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_UTILITY?.getLenders();
    return request('get', MORTGAGE_API_CONFIG?.ENDPOINTS?.UTILITY?.LENDERS);
  },

  /**
   * Get current RBA and average market interest rates.
   */
  getInterestRates: async () => {
    if (MORTGAGE_API_CONFIG?.USE_MOCK) return MOCK_UTILITY?.getInterestRates();
    return request('get', MORTGAGE_API_CONFIG?.ENDPOINTS?.UTILITY?.INTEREST_RATES);
  },
};

// ─── Utility Helpers ──────────────────────────────────────────────────────────

export const mortgageHelpers = {
  /**
   * Format a number as AUD currency string.
   */
  formatCurrency: (amount, locale = 'en-AU', currency = 'AUD') =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount),

  /**
   * Calculate LVR (Loan-to-Value Ratio).
   */
  calculateLVR: (loanAmount, propertyValue) =>
    propertyValue > 0 ? parseFloat(((loanAmount / propertyValue) * 100)?.toFixed(2)) : 0,

  /**
   * Determine if LMI (Lenders Mortgage Insurance) applies.
   */
  requiresLMI: (loanAmount, propertyValue) =>
    mortgageHelpers?.calculateLVR(loanAmount, propertyValue) > 80,

  /**
   * Estimate stamp duty (simplified — varies by state).
   */
  estimateStampDuty: (propertyPrice, state = 'NSW') => {
    // Simplified NSW calculation — professor's backend should provide accurate figures
    if (propertyPrice <= 14000) return Math.max(10, propertyPrice * 0.0125);
    if (propertyPrice <= 30000) return 175 + (propertyPrice - 14000) * 0.015;
    if (propertyPrice <= 80000) return 415 + (propertyPrice - 30000) * 0.0175;
    if (propertyPrice <= 300000) return 1290 + (propertyPrice - 80000) * 0.035;
    if (propertyPrice <= 1000000) return 8990 + (propertyPrice - 300000) * 0.045;
    return 40490 + (propertyPrice - 1000000) * 0.055;
  },

  /**
   * Check if the API is running in mock mode.
   */
  isMockMode: () => MORTGAGE_API_CONFIG?.USE_MOCK,
};

export default {
  auth: mortgageAuthService,
  mortgage: mortgageService,
  utility: mortgageUtilityService,
  helpers: mortgageHelpers,
};
